/**
 * searchIndex.js — what the global search can find, and how it ranks it
 * ======================================================================
 * Usability review of 23 October 2026, item 2: *"One box where typing
 * "Valuno", "DEVS" or a ticker jumps straight to the position, the deal
 * report or the ledger."*
 *
 * Kept apart from the palette so the matching can be tested without a
 * browser. Five kinds of entry:
 *
 *   Deal       a funded investment         → its deal report
 *   Position   a holding the fund has now  → the Dashboard, filtered to it
 *   Security   an instrument on the book   → its deal's report, or Positions
 *   Investor   a lender                    → Investors
 *   Page       a screen in the sidebar     → that screen
 *
 * A ticker is matched with its exchange suffix and punctuation folded away,
 * so "DEVS" finds DEVSF and "shfs" finds "SHFS US Equity" (§0.9: a ticker
 * changes on a delisting, so the prefix is what a reader remembers).
 */
import { dealReportPath, positionsPath, ledgerPath, dashboardPath, securitiesPath } from "./links";
import { money, quantity } from "./format";

export const fold = (text) =>
  String(text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+us\s+equity\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const tickerKey = (ticker) => fold(ticker).replace(/\s+/g, "");

const KIND_ORDER = { Deal: 0, Position: 1, Security: 2, Investor: 3, Page: 4 };

/** Build the searchable entries from what the server returned. */
export function buildIndex({ pages = [], deals = [], securities = [], positions = [], investors = [] }) {
  const entries = [];
  const dealBySecurity = new Map();
  for (const d of deals) {
    if (d.security !== null && d.security !== undefined && !dealBySecurity.has(d.security)) {
      dealBySecurity.set(d.security, d);
    }
  }

  for (const d of deals) {
    const name = d.deal_name || `Deal ${d.id}`;
    entries.push({
      kind: "Deal",
      key: `deal:${d.id}`,
      label: name,
      sub: [d.counterparty, d.security_ticker, d.status, d.strategy_tag].filter(Boolean).join(" · "),
      ticker: d.security_ticker || "",
      terms: [name, d.counterparty, d.security_ticker, d.strategy_tag, d.instrument_type],
      to: dealReportPath(d.id),
      extra: [
        { label: "Ledger", to: ledgerPath(name) },
        { label: "Positions", to: positionsPath(d.security_ticker || name) },
      ],
    });
  }

  // One entry per instrument held, not per tax lot.
  const held = new Map();
  for (const p of positions) {
    const id = p.security_id ?? `${p.ticker}|${p.name}`;
    const row = held.get(id) || { ...p, quantity: 0, mv_usd: 0, usdKnown: true };
    row.quantity += Number(p.quantity) || 0;
    if (p.mv_usd === null || p.mv_usd === undefined || p.mv_usd === "") row.usdKnown = false;
    else row.mv_usd += Number(p.mv_usd) || 0;
    held.set(id, row);
  }
  for (const p of held.values()) {
    entries.push({
      kind: "Position",
      key: `position:${p.security_id ?? p.ticker}`,
      label: p.name || p.ticker,
      sub: [p.ticker, `${quantity(p.quantity)} held`, p.usdKnown ? money(p.mv_usd, { whole: true }) : null]
        .filter(Boolean).join(" · "),
      ticker: p.ticker || "",
      terms: [p.name, p.ticker, p.deal_name, p.instrument_type],
      to: dashboardPath(p.ticker || p.name),
      extra: p.deal_id ? [{ label: "Deal report", to: dealReportPath(p.deal_id) }] : [],
    });
  }

  for (const s of securities) {
    const deal = dealBySecurity.get(s.id);
    entries.push({
      kind: "Security",
      key: `security:${s.id}`,
      label: s.name || s.ticker,
      sub: [s.ticker, s.issuer, s.instrument_type].filter(Boolean).join(" · "),
      ticker: s.ticker || "",
      terms: [s.name, s.ticker, s.issuer, s.instrument_type, s.yahoo_symbol],
      to: deal ? dealReportPath(deal.id) : positionsPath(s.ticker || s.name),
      extra: [
        { label: "Positions", to: positionsPath(s.ticker || s.name) },
        { label: "Securities", to: securitiesPath(s.ticker || s.name) },
      ],
    });
  }

  for (const i of investors) {
    const name = i.investor_name || i.name;
    if (!name) continue;
    entries.push({
      kind: "Investor",
      key: `investor:${i.id}`,
      label: name,
      sub: [i.investor_type, i.entity_name].filter(Boolean).join(" · "),
      ticker: "",
      terms: [name, i.contact_name],
      to: "/investors",
      extra: [{ label: "Ledger", to: ledgerPath(name) }],
    });
  }

  for (const p of pages) {
    entries.push({
      kind: "Page",
      key: `page:${p.to}`,
      label: p.label,
      sub: p.section ? `${p.section.charAt(0)}${p.section.slice(1).toLowerCase()}` : "Main",
      ticker: "",
      terms: [p.label, p.section],
      to: p.to,
      extra: [],
    });
  }

  return entries.map((e) => ({ ...e, haystack: fold(e.terms.filter(Boolean).join(" ")), tickerKey: tickerKey(e.ticker) }));
}

/** How well one entry answers a query; 0 means it does not. */
export function score(entry, query) {
  const q = fold(query);
  if (!q) return 0;
  const words = q.split(" ");
  if (!words.every((w) => entry.haystack.includes(w))) return 0;
  const compact = q.replace(/\s+/g, "");
  const label = fold(entry.label);
  let s = 10;
  if (entry.tickerKey && entry.tickerKey === compact) s += 100;
  else if (entry.tickerKey && entry.tickerKey.startsWith(compact)) s += 60;
  if (label === q) s += 80;
  else if (label.startsWith(q)) s += 40;
  else if (` ${label}`.includes(` ${words[0]}`)) s += 20;
  return s;
}

/** The best matches, at most `perKind` of each kind and `limit` in all. */
export function search(entries, query, { perKind = 6, limit = 24 } = {}) {
  const scored = entries
    .map((e) => ({ e, s: score(e, query) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || KIND_ORDER[a.e.kind] - KIND_ORDER[b.e.kind] || a.e.label.localeCompare(b.e.label));
  const counts = {};
  const out = [];
  for (const { e } of scored) {
    counts[e.kind] = (counts[e.kind] || 0) + 1;
    if (counts[e.kind] > perKind) continue;
    out.push(e);
    if (out.length >= limit) break;
  }
  // Listed kind by kind, so each kind has one heading. A kind comes where its
  // best match ranks, and keeps its own order inside.
  const firstAt = {};
  out.forEach((e, i) => { if (!(e.kind in firstAt)) firstAt[e.kind] = i; });
  return out
    .map((e, i) => ({ e, i }))
    .sort((a, b) => firstAt[a.e.kind] - firstAt[b.e.kind] || a.i - b.i)
    .map(({ e }) => e);
}
